import React, { useEffect, useRef } from 'react';

const isClickWithinElement = (element: HTMLElement, event: MouseEvent) => {
  const { clientX, clientY } = event;
  const { left, top, right, bottom } = element.getBoundingClientRect();
  return (
    clientX >= left && clientX <= right && clientY >= top && clientY <= bottom
  );
};

type UseOnClickOutsideOptions = {
  stopPropagation?: boolean;
};

export const useOnClickOutside = (
  ref: React.RefObject<HTMLElement | null>,
  onClickOutside: VoidFunction,
  options?: UseOnClickOutsideOptions
) => {
  const syntheticEventRef = useRef(false);
  const stopPropagation = options?.stopPropagation ?? false;

  useEffect(() => {
    const currentElement = ref.current;

    // Listen to clicks on "inside" node taking into account nodes rendered through React.Portal
    const onCurrentElementClick = () => {
      syntheticEventRef.current = true;
    };

    const handleMouseEvent = (event: MouseEvent) => {
      const insideReactTree = syntheticEventRef.current;
      syntheticEventRef.current = false;

      const isOutside =
        ref.current &&
        event.target instanceof Node &&
        // A click whose target is <body>/<html> itself, rather than some
        // visible element, means whatever was under the cursor was removed
        // from the DOM between mousedown and click (e.g. a portaled menu
        // option closing on select) - not a deliberate outside click.
        event.target !== document.body &&
        event.target !== document.documentElement &&
        !ref.current.contains(event.target) &&
        // If the click is over the ref element position, don't call onClickOutside
        // This can happen when element rendered via portal is clicked
        !isClickWithinElement(ref.current, event) &&
        !insideReactTree;

      if (isOutside) {
        if (stopPropagation) {
          event.stopPropagation();
        }
        if (event.type === 'click') {
          onClickOutside();
        }
      }
    };

    currentElement?.addEventListener('click', onCurrentElementClick);

    if (stopPropagation) {
      // When stopPropagation is enabled, listen to mousedown, mouseup, and click
      // to prevent clicks from reaching elements below
      document.addEventListener('mousedown', handleMouseEvent, true);
      document.addEventListener('mouseup', handleMouseEvent, true);
      document.addEventListener('click', handleMouseEvent, true);
    } else {
      document.addEventListener('click', handleMouseEvent);
    }

    return () => {
      currentElement?.removeEventListener('click', onCurrentElementClick);
      if (stopPropagation) {
        document.removeEventListener('mousedown', handleMouseEvent, true);
        document.removeEventListener('mouseup', handleMouseEvent, true);
        document.removeEventListener('click', handleMouseEvent, true);
      } else {
        document.removeEventListener('click', handleMouseEvent);
      }
    };
  }, [ref, onClickOutside, stopPropagation]);
};
